import { useState, useEffect, useMemo, useRef } from 'react';
import { useScore } from '../../../context/ScoreContext';


export default function MemberProfileModal() {
  const {
    showMemberModal,
    setShowMemberModal,
    pendingTeam,
    addOrUpdateMember,
    updateMemberAvatar,
    currentMember,
    selectedTeam,
    members,
    myMemberId,
    scheduleState,
  } = useScore();

  const [name, setName] = useState('');
  const [goal, setGoal] = useState('');
  const [nameError, setNameError] = useState('');
  const [showMissionRules, setShowMissionRules] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [localAvatarUrl, setLocalAvatarUrl] = useState<string | undefined>(undefined);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const hasPendingUploadRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Entrance animation ──────────────────────────────────────────────────
  // New member: wait for JoinCelebration to signal (srs-join-done)
  // Editing: enter immediately
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!showMemberModal) {
      setEntered(false);
      return;
    }
    if (currentMember) {
      // Editing: use double-rAF so "from" state paints before animating — no setTimeout lag
      setEntered(false);
      let raf1: number, raf2: number;
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setEntered(true));
      });
      return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
    } else {
      // New member: JoinCelebration plays first, listen for its fade-start signal
      setEntered(false);
      const handler = () => setEntered(true);
      window.addEventListener('srs-join-done', handler, { once: true });
      // Fallback: if join event never fires within 2s, show anyway
      const fallback = setTimeout(() => setEntered(true), 2000);
      return () => {
        window.removeEventListener('srs-join-done', handler);
        clearTimeout(fallback);
      };
    }
  }, [showMemberModal, currentMember?.id]); // include currentMember?.id so stale closure won't cause invisible modal

  const team = pendingTeam ?? selectedTeam;
  const isEditing = !!currentMember;

  // Secret mission — 只要有配對就顯示（不需等揭曉特效解鎖）
  const { secretPairings } = scheduleState;
  const targetId = myMemberId ? secretPairings[myMemberId] : undefined;
  const targetMember = useMemo(
    () => (targetId ? members.find(m => m.id === targetId) : null),
    [targetId, members],
  );
  // 有配對目標就視為任務啟動
  const hasMission = !!targetId;

  useEffect(() => {
    if (showMemberModal && currentMember) {
      setName(currentMember.name);
      setGoal(currentMember.goal);
      setLocalAvatarUrl(currentMember.avatarUrl);
    } else if (showMemberModal) {
      setName('');
      setGoal('');
      setLocalAvatarUrl(undefined);
      setPendingFile(null);
      hasPendingUploadRef.current = false;
    }
    setNameError('');
    setUploadError('');
    setShowMissionRules(false);
  }, [showMemberModal, currentMember]);

  // 新成員：等 myMemberId 設定後上傳待傳的大頭照
  useEffect(() => {
    if (hasPendingUploadRef.current && myMemberId && pendingFile) {
      hasPendingUploadRef.current = false;
      const file = pendingFile;
      setPendingFile(null);
      setUploading(true);
      updateMemberAvatar(myMemberId, file)
        .catch(() => setUploadError('大頭照上傳失敗，稍後可在個人資料重試'))
        .finally(() => setUploading(false));
    }
  }, [myMemberId, pendingFile, updateMemberAvatar]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('圖片不能超過 5MB');
      return;
    }
    setUploadError('');
    const previewUrl = URL.createObjectURL(file);
    setLocalAvatarUrl(previewUrl);

    if (myMemberId) {
      // 編輯模式：立即上傳
      setUploading(true);
      try {
        await updateMemberAvatar(myMemberId, file);
      } catch {
        setUploadError('上傳失敗，請再試一次');
        setLocalAvatarUrl(currentMember?.avatarUrl);
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    } else {
      // 新成員：先存起來，送出後再上傳
      setPendingFile(file);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (!showMemberModal || !team) return null;

  const isBlack = team === 'black';
  const accentColor = isBlack ? '#d4a843' : '#e8e8e8';
  const teamLabel = isBlack ? '黑隊' : '白隊';
  const teamBadge = isBlack
    ? 'bg-[#d4a843] text-[#0d0d0d]'
    : 'bg-white text-[#0d0d0d]';

  const handleSubmit = () => {
    if (!name.trim()) {
      setNameError('請輸入你的名字');
      return;
    }
    // 若有待上傳的大頭照，標記等 myMemberId 出現後上傳
    if (!isEditing && pendingFile) {
      hasPendingUploadRef.current = true;
    }
    // 傳入 localAvatarUrl，讓成員資料立即顯示大頭照（含 blob URL）
    addOrUpdateMember(name.trim(), goal.trim(), team, localAvatarUrl ?? undefined);
  };

  const handleSkip = () => {
    setShowMemberModal(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div
        className="absolute inset-0 bg-[#050505]/90 backdrop-blur-sm"
      />

      {/* Mission Rules Popup */}
      {showMissionRules && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center p-4"
          onClick={() => setShowMissionRules(false)}
        >
          <div
            className="relative w-full max-w-sm rounded-2xl border overflow-hidden flex flex-col"
            style={{ borderColor: '#c06b9a50', background: '#111', maxHeight: '80dvh' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header — 固定不滾動 */}
            <div className="px-5 pt-5 pb-4 border-b shrink-0" style={{ borderColor: '#c06b9a20', background: '#c06b9a08' }}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full" style={{ background: '#c06b9a20' }}>
                    <i className="ri-spy-fill text-sm" style={{ color: '#c06b9a' }} />
                  </div>
                  <span className="text-white font-black text-base" style={{ fontFamily: 'Georgia, serif' }}>小主人任務</span>
                </div>
                <button
                  onClick={() => setShowMissionRules(false)}
                  className="w-7 h-7 flex items-center justify-center rounded-full border border-[#2a2a2a] text-[#666] hover:text-white cursor-pointer transition-colors"
                >
                  <i className="ri-close-line text-sm" />
                </button>
              </div>
              <p className="text-[#c06b9a]/60 text-[10px] tracking-widest uppercase ml-9">Secret Angel Mission</p>
            </div>

            {/* Rules content — 可滾動 */}
            <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1">
              <div className="rounded-xl p-4 border" style={{ borderColor: '#c06b9a25', background: '#c06b9a06' }}>
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-gift-line text-base" style={{ color: '#c06b9a' }} />
                  <span className="text-white text-sm font-bold">任務說明</span>
                </div>
                <p className="text-[#bbb] text-sm leading-relaxed">
                  請在老街準備 <span className="text-white font-bold">100 元</span> 的禮物給小主人，並在一整天的時間默默關心他，<span className="text-[#c06b9a] font-bold">夜間會指認小天使並送禮</span>。
                </p>
              </div>
              <div className="rounded-xl p-4 border" style={{ borderColor: '#d4a84325', background: '#d4a84306' }}>
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-medal-line text-base text-[#d4a843]" />
                  <span className="text-white text-sm font-bold">獎勵機制</span>
                </div>
                <div className="space-y-2.5">
                  <div className="flex items-start gap-2.5">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: '#c06b9a20' }}>
                      <i className="ri-user-heart-line text-[10px]" style={{ color: '#c06b9a' }} />
                    </div>
                    <p className="text-[#aaa] text-xs leading-relaxed">
                      <span className="text-white font-bold">小主人猜對小天使</span> → 可為隊伍加分
                    </p>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: '#7ec8a020' }}>
                      <i className="ri-heart-line text-[10px] text-[#7ec8a0]" />
                    </div>
                    <p className="text-[#aaa] text-xs leading-relaxed">
                      <span className="text-white font-bold">小主人滿意禮物</span> → 可為小天使隊伍加分
                    </p>
                  </div>
                </div>
              </div>
              {targetMember && (
                <div className="rounded-xl p-3 border flex items-center gap-3" style={{ borderColor: '#c06b9a30', background: '#c06b9a0a' }}>
                  <div className="w-1 h-10 rounded-full shrink-0" style={{ background: '#c06b9a60' }} />
                  <div>
                    <p className="text-[#888] text-[10px] mb-0.5">你的小主人是</p>
                    <p className="text-white text-sm font-black">{targetMember.name}</p>
                    <p className="text-[#c06b9a] text-[10px]">悄悄地關心他 ♥</p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer — 固定不滾動 */}
            <div className="px-5 py-4 shrink-0 border-t" style={{ borderColor: '#c06b9a15' }}>
              <button
                onClick={() => setShowMissionRules(false)}
                className="w-full py-3 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap text-[#0d0d0d]"
                style={{ background: '#c06b9a' }}
              >
                了解！開始秘密行動
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className="relative z-10 w-full sm:max-w-lg sm:mx-4"
        style={{
          transform: entered ? 'translateY(0)' : 'translateY(24px)',
          opacity: entered ? 1 : 0,
          transition: entered
            ? 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.25s ease'
            : 'none',
        }}
      >
        {/* mobile drag handle */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-[#444]" />
        </div>

        <div
          className="rounded-t-2xl sm:rounded-xl border-t sm:border overflow-y-auto"
          style={{
            borderColor: `${accentColor}33`,
            background: '#111',
            maxHeight: '92dvh',
          }}
        >
          <div className="px-5 pt-4 pb-7 sm:p-8">
            {/* header */}
            <div className="text-center mb-4 sm:mb-7">
              <div className="flex items-center justify-center gap-2 mb-3">
                <span className={`inline-block text-xs tracking-[0.4em] uppercase font-bold px-3 py-1 rounded-full ${teamBadge}`}>
                  {teamLabel} 成員
                </span>
                {isEditing && (
                  <span className="inline-flex items-center gap-1 text-[10px] text-[#555] bg-[#1a1a1a] border border-[#2a2a2a] px-2 py-1 rounded-full whitespace-nowrap">
                    <i className="ri-lock-line" />
                    隊伍已鎖定
                  </span>
                )}
              </div>
              <h2 className="text-white text-xl sm:text-2xl font-black mb-1.5" style={{ fontFamily: 'Georgia, serif' }}>
                {isEditing ? '編輯你的資料' : '留下你的足跡'}
              </h2>
              <p className="text-[#666] text-xs sm:text-sm">
                {isEditing ? '名字與目標可以隨時更新' : '讓隊友認識你，也讓自己記住這次出征的意義'}
              </p>
            </div>

            {/* avatar — 新成員 & 編輯都可上傳 */}
            <div className="flex flex-col items-center mb-4 sm:mb-7">
              <label htmlFor="profile-avatar-input" className="relative cursor-pointer group">
                {localAvatarUrl ? (
                  <img
                    src={localAvatarUrl}
                    alt={name}
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-2 object-cover object-top transition-opacity group-hover:opacity-80"
                    style={{ borderColor: accentColor }}
                  />
                ) : (
                  <div
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center border-2 text-2xl sm:text-3xl font-black select-none transition-opacity group-hover:opacity-80"
                    style={{ borderColor: accentColor, color: accentColor, background: `${accentColor}15` }}
                  >
                    {name.trim() ? name.trim().charAt(0).toUpperCase() : '?'}
                  </div>
                )}

                {/* 相機徽章 — 永遠顯示 */}
                <div
                  className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full flex items-center justify-center border-2 border-[#111] transition-transform group-hover:scale-110"
                  style={{ background: uploading ? '#444' : accentColor }}
                >
                  {uploading ? (
                    <i className="ri-loader-4-line text-[10px] text-white animate-spin" />
                  ) : (
                    <i className="ri-camera-fill text-[10px]" style={{ color: '#0d0d0d' }} />
                  )}
                </div>

                <input
                  id="profile-avatar-input"
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={handleFileChange}
                />
              </label>

              <p className="text-[#555] text-[10px] mt-2">
                {uploading
                  ? '上傳中…'
                  : uploadError
                  ? uploadError
                  : localAvatarUrl
                  ? '點頭像可更換照片'
                  : '點此上傳大頭照（選填）'}
              </p>
            </div>

            {/* name input */}
            <div className="mb-3 sm:mb-5">
              <label className="block text-xs tracking-widest text-[#888] uppercase mb-2">
                你的名字 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={e => { setName(e.target.value); setNameError(''); }}
                placeholder="輸入名字或暱稱"
                maxLength={20}
                className="w-full bg-[#1a1a1a] border rounded-lg px-4 py-2.5 text-white text-sm placeholder-[#555] outline-none focus:ring-1 transition-all"
                style={{ borderColor: nameError ? '#ef4444' : '#2a2a2a' }}
                onFocus={e => (e.target.style.borderColor = accentColor)}
                onBlur={e => (e.target.style.borderColor = nameError ? '#ef4444' : '#2a2a2a')}
              />
              {nameError && <p className="text-red-400 text-xs mt-1">{nameError}</p>}
            </div>

            {/* goal input */}
            <div className="mb-3 sm:mb-5">
              <label className="block text-xs tracking-widest text-[#888] uppercase mb-2">
                這次的個人目標
              </label>
              <textarea
                value={goal}
                onChange={e => setGoal(e.target.value)}
                placeholder="例：讓隊友因為我而勝利、突破自己的舒適圈..."
                maxLength={80}
                rows={2}
                className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-2.5 text-white text-sm placeholder-[#555] outline-none resize-none transition-all"
                onFocus={e => (e.target.style.borderColor = accentColor)}
                onBlur={e => (e.target.style.borderColor = '#2a2a2a')}
              />
              <p className="text-right text-[#555] text-xs mt-1">{goal.length} / 80</p>
            </div>

            {/* Secret mission card — only shown when editing & mission is active */}
            {isEditing && hasMission && (
              <button
                onClick={() => setShowMissionRules(true)}
                className="w-full text-left rounded-xl p-3 border mb-4 cursor-pointer transition-all hover:border-[#c06b9a60] active:scale-[0.98] group"
                style={{ borderColor: '#c06b9a40', background: '#c06b9a08' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-spy-line text-xs" style={{ color: '#c06b9a' }} />
                  </div>
                  <span className="text-[10px] tracking-widest uppercase font-bold" style={{ color: '#c06b9a' }}>
                    小主人任務
                  </span>
                  <span
                    className="ml-auto flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap"
                    style={{ background: '#c06b9a20', color: '#c06b9a' }}
                  >
                    <i className="ri-question-line" />
                    查看規則
                  </span>
                </div>
                {targetMember ? (
                  <div className="flex items-center gap-2.5 rounded-lg px-2.5 py-2"
                    style={{
                      background: '#0f0f0f',
                      border: `1px solid ${targetMember.team === 'black' ? '#d4a84330' : '#e0e0e030'}`,
                    }}
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-black shrink-0"
                      style={{
                        background: targetMember.team === 'black' ? '#d4a84320' : '#e0e0e015',
                        color: targetMember.team === 'black' ? '#d4a843' : '#e0e0e0',
                        border: `1px solid ${targetMember.team === 'black' ? '#d4a84340' : '#e0e0e030'}`,
                      }}
                    >
                      {targetMember.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[#888] text-[10px] mb-0.5">你的秘密目標</p>
                      <p className="text-white text-sm font-black truncate">{targetMember.name}</p>
                    </div>
                    <i className="ri-arrow-right-s-line text-[#c06b9a] shrink-0 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                ) : (
                  <p className="text-[#444] text-xs italic">本次未獲得配對目標</p>
                )}
              </button>
            )}

            {/* actions */}
            <div className="flex gap-3">
              <button
                onClick={handleSkip}
                className="flex-1 py-3 rounded-lg border border-[#2a2a2a] text-[#666] text-sm hover:border-[#444] hover:text-[#999] transition-all cursor-pointer whitespace-nowrap"
              >
                {isEditing ? '關閉' : '暫時跳過'}
              </button>
              <button
                onClick={handleSubmit}
                className="flex-2 flex-grow py-3 rounded-lg text-sm font-bold tracking-wider transition-all cursor-pointer whitespace-nowrap"
                style={{ background: accentColor, color: '#0d0d0d' }}
              >
                {isEditing ? '儲存更新' : '加入戰局'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
